import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, CheckCircle, AlertTriangle, XCircle } from "lucide-react";

const getScoreConfig = (score) => {
  if (score >= 7) {
    return {
      color: "hsl(var(--success))",
      bgColor: "hsl(var(--success) / 0.1)",
      textClass: "text-success",
      ringClass: "stroke-success",
      label: "Excellent"
    };
  } else if (score >= 5) {
    return {
      color: "hsl(var(--warning))",
      bgColor: "hsl(var(--warning) / 0.1)",
      textClass: "text-warning",
      ringClass: "stroke-warning",
      label: "Good"
    };
  } else {
    return {
      color: "hsl(var(--destructive))",
      bgColor: "hsl(var(--destructive) / 0.1)",
      textClass: "text-destructive",
      ringClass: "stroke-destructive",
      label: "Needs Work"
    };
  }
};

const getVerdictConfig = (verdict) => {
  const verdictLower = (verdict || "").toLowerCase();
  
  if (verdictLower.includes("viral") || verdictLower.includes("fire")) {
    return {
      icon: Flame,
      emoji: "🔥",
      label: "Viral Potential",
      className: "bg-success/10 text-success border-success/20"
    };
  } else if (verdictLower.includes("solid") || verdictLower.includes("good")) {
    return {
      icon: CheckCircle,
      emoji: "✅",
      label: "Solid Post",
      className: "bg-primary/10 text-primary border-primary/20"
    };
  } else if (verdictLower.includes("needs") || verdictLower.includes("work")) {
    return {
      icon: AlertTriangle,
      emoji: "⚠️",
      label: "Needs Work",
      className: "bg-warning/10 text-warning border-warning/20"
    };
  } else {
    return {
      icon: XCircle,
      emoji: "❌",
      label: "Rethink This",
      className: "bg-destructive/10 text-destructive border-destructive/20"
    };
  }
};

export const ScoreDisplay = ({ score, verdict }) => {
  // Ensure score is a valid number between 0-10
  const numericScore = typeof score === 'number' ? score : (parseFloat(score) || 0);
  const clampedScore = Math.max(0, Math.min(10, numericScore));
  
  const scoreConfig = useMemo(() => getScoreConfig(clampedScore), [clampedScore]);
  const verdictConfig = useMemo(() => getVerdictConfig(verdict), [verdict]);
  
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (clampedScore / 10) * circumference;

  return (
    <Card className="border border-border shadow-md overflow-hidden">
      <CardContent className="p-6 sm:p-8">
        <div className="flex flex-col items-center gap-6">
          {/* Circular Score */}
          <div className="relative">
            {/* Background glow */}
            <div 
              className="absolute inset-0 rounded-full blur-xl opacity-30"
              style={{ backgroundColor: scoreConfig.color }}
            />
            
            <svg 
              className="w-32 h-32 sm:w-40 sm:h-40 transform -rotate-90" 
              viewBox="0 0 100 100"
              aria-label={`Score: ${score} out of 10`}
            >
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeWidth="8"
                className="stroke-muted"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                style={{
                  stroke: scoreConfig.color,
                  strokeDasharray: circumference,
                  strokeDashoffset: strokeDashoffset,
                  transition: "stroke-dashoffset 0.5s ease-out"
                }}
              />
            </svg>
            
            {/* Score number */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span 
                className="text-4xl sm:text-5xl font-bold"
                style={{ color: scoreConfig.color }}
              >
                {clampedScore.toFixed(1)}
              </span>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                out of 10
              </span>
            </div>
          </div>

          {/* Verdict Badge */}
          <Badge 
            variant="outline"
            className={`px-4 py-2 text-sm font-medium border ${verdictConfig.className}`}
          >
            <span className="mr-2">{verdictConfig.emoji}</span>
            {verdict || verdictConfig.label}
          </Badge>

          {/* Score label */}
          <p className="text-sm text-muted-foreground">
            Your post scores in the <span className={`font-semibold ${scoreConfig.textClass}`}>{scoreConfig.label}</span> range
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoreDisplay;
